import { CalendarEvent } from '../../types/calendar';
import MemberAvatar from '../MemberAvatar';
import { formatRecurringPattern } from '../../utils/recurrence';
import { getBaseEventId, isExpandedRecurringInstance } from '../../utils/recurringInstance';
import { formatTime, formatDate } from '../../utils/time';
import { Icon } from '../../utils/icons';
import { EventTodoPanel } from '../todos/EventTodoPanel';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';

interface EventPreviewProps {
  event: CalendarEvent;
  onEdit: () => void;
  onClose: () => void;
  onDelete?: () => void;
  isDarkMode?: boolean;
  isDeleting?: boolean;
}

export default function EventPreview({
  event,
  onEdit,
  onClose,
  onDelete,
  isDarkMode = false,
  isDeleting = false,
}: EventPreviewProps) {
  const members = event.members && event.members.length > 0
    ? event.members
    : (event.memberId ? [{
        memberId: event.memberId,
        memberName: event.memberName || 'Unknown',
        memberColor: event.memberColor || '#3b82f6',
        memberAvatar: event.memberAvatar,
      }] : []);

  const startDateObj = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
  const endDateObj = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
  
  // Check if this is an all-day event
  // All-day events are stored as UTC midnight to 23:59:59, which can appear as different dates/times
  // in local timezone, so we need to check the isAllDay flag or detect from the stored format
  const isAllDay = event.isAllDay === true || event.isImportantDate === true;
  
  // For all-day events, extract the date from the UTC string before converting to local time
  // This ensures we show the correct date regardless of timezone
  let startDate: string;
  let endDate: string;
  let startTime: string | null = null;
  let endTime: string | null = null;
  let startDateStr: string;
  let endDateStr: string;
  
  if (isAllDay) {
    // For all-day events, extract date using local timezone methods to avoid UTC conversion issues
    // The Date objects were already created in local timezone from date-only strings
    const startTimeValue = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const endTimeValue = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    
    // Extract date strings using local timezone (not UTC) to preserve the correct date
    startDateStr = `${startTimeValue.getFullYear()}-${String(startTimeValue.getMonth() + 1).padStart(2, '0')}-${String(startTimeValue.getDate()).padStart(2, '0')}`;
    endDateStr = `${endTimeValue.getFullYear()}-${String(endTimeValue.getMonth() + 1).padStart(2, '0')}-${String(endTimeValue.getDate()).padStart(2, '0')}`;
    
    // Use the Date objects directly for formatting (they're already in local timezone)
    startDate = formatDate(startTimeValue, 'long');
    endDate = formatDate(endTimeValue, 'long');
  } else {
    // For timed events, use the converted local timezone dates
    startDate = formatDate(startDateObj, 'long');
    startTime = formatTime(startDateObj);
    endDate = formatDate(endDateObj, 'long');
    endTime = formatTime(endDateObj);
    // Extract date strings for same-day comparison
    startDateStr = startDateObj.toISOString().split('T')[0];
    endDateStr = endDateObj.toISOString().split('T')[0];
  }
  
  // Check if same day (for all-day events, compare date strings; for timed events, compare Date objects)
  const isSameDay = startDateStr === endDateStr;
  const isMultiDay = !isSameDay;
  const isExpandedInstance = isExpandedRecurringInstance(event);
  const baseId = getBaseEventId(event.id);
  const recurringPatternText = event.isRecurring ? formatRecurringPattern(event.recurringPattern) : '';

  const { isDarkMode: storeIsDarkMode } = useThemeStore();
  const currentIsDarkMode = isDarkMode ?? storeIsDarkMode;
  const tokens = getDesignTokens(currentIsDarkMode);

  const theme = currentIsDarkMode
    ? {
        bg: 'bg-gray-800/95',
        text: 'text-gray-100',
        textMuted: 'text-gray-300',
        border: tokens.border.light,
        button: tokens.button.secondary,
        accent: tokens.button.primary,
      }
    : {
        bg: 'bg-white',
        text: 'text-gray-800',
        textMuted: 'text-gray-600',
        border: tokens.border.light,
        button: tokens.button.secondary,
        accent: tokens.button.primary,
      };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1" onClick={onClose}>
      <div
        className={`${theme.bg} ${theme.border} border rounded-landing ${tokens.card.shadow} max-w-2xl w-full max-h-[95vh] overflow-y-auto p-2`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className={`${theme.text} text-lg sm:text-xl font-bold`}>{event.title}</h2>
          <button
            onClick={onClose}
            className={`${theme.button} w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95`}
            aria-label="Close"
          >
            <Icon name="close" size="md" className={theme.text} />
          </button>
        </div>

        {/* Event Details */}
        <div className="space-y-1.5">
          {/* Multi-day Event Notice */}
          {isMultiDay && (
            <div className={`${theme.border} border rounded p-1.5 bg-yellow-100 dark:bg-yellow-900/40`}>
              <div className="flex items-start gap-2">
                <div className="text-lg flex-shrink-0">📋</div>
                <div className="flex-1">
                  <div className={`text-yellow-900 dark:text-yellow-100 text-xs font-semibold`}>
                    Multi-Day Event
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Date and Time */}
          <div className={`${theme.border} border rounded p-1.5`}>
            <div className="flex items-start gap-2">
              <div className="text-base">⏰</div>
              <div className="flex-1">
                <div className={`${theme.textMuted} text-xs font-semibold mb-0.5`}>Date & Time</div>
                <div className={`${theme.text} text-xs font-medium`}>
                  {isAllDay ? (
                    // All-day events: show only dates, no times
                    isSameDay ? (
                      <div>{startDate}</div>
                    ) : (
                      <>
                        <div className="mb-1">
                          <div className="font-semibold">Start:</div>
                          <div>{startDate}</div>
                        </div>
                        <div>
                          <div className="font-semibold">End:</div>
                          <div>{endDate}</div>
                        </div>
                      </>
                    )
                  ) : (
                    // Timed events: show dates and times
                    isSameDay ? (
                      <>
                        <div>{startDate}</div>
                        <div className="mt-0.5">{startTime} - {endTime}</div>
                      </>
                    ) : (
                      <>
                        <div className="mb-1">
                          <div className="font-semibold">Start:</div>
                          <div>{startDate}</div>
                          <div>{startTime}</div>
                        </div>
                        <div>
                          <div className="font-semibold">End:</div>
                          <div>{endDate}</div>
                          <div>{endTime}</div>
                        </div>
                      </>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recurrence */}
          {event.isRecurring && (
            <div className={`${theme.border} border rounded p-1.5`}>
              <div className="flex items-start gap-2">
                <div className="text-base opacity-75">↻</div>
                <div className="flex-1">
                  <div className={`${theme.textMuted} text-xs font-semibold mb-0.5`}>Recurring</div>
                  <div className={`${theme.text} text-xs font-medium`}>
                    {recurringPatternText || 'Recurring'}
                  </div>
                  {isExpandedInstance && (
                    <div className={`${theme.textMuted} text-[10px] mt-1`}>
                      This is an occurrence of a recurring event. Edits and deletes apply to the whole series.
                      <span className="block mt-0.5 opacity-80">Series ID: {baseId}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Family Members */}
          {members.length > 0 && (
            <div className={`${theme.border} border rounded p-1.5`}>
              <div className="flex items-start gap-2">
                <div className="text-base">👥</div>
                <div className="flex-1">
                  <div className={`${theme.textMuted} text-xs font-semibold mb-1`}>
                    {members.length === 1 ? 'Family Member' : 'Family Members'}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {members.map((member) => (
                      <div
                        key={member.memberId}
                        className="flex items-center gap-1.5 px-2 py-1 rounded border"
                        style={{ borderColor: member.memberColor }}
                      >
                        <MemberAvatar
                          member={member}
                          size="sm"
                        />
                        <div className={`${theme.text} text-xs font-semibold`}>
                          {member.memberName}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className={`${theme.border} border rounded p-1.5`}>
              <div className="flex items-start gap-2">
                <div className="text-base">📍</div>
                <div className="flex-1">
                  <div className={`${theme.textMuted} text-xs font-semibold mb-0.5`}>Location</div>
                  <div className={`${theme.text} text-xs font-medium`}>{event.location}</div>
                </div>
              </div>
            </div>
          )}

          {/* Description - Always show */}
          <div className={`${theme.border} border rounded p-1.5`}>
            <div className="flex items-start gap-2">
              <div className="text-base">📝</div>
              <div className="flex-1">
                <div className={`${theme.textMuted} text-xs font-semibold mb-0.5`}>Description</div>
                <div className={`${theme.text} text-xs whitespace-pre-wrap ${!event.description ? 'italic opacity-60' : ''}`}>
                  {event.description || 'No description provided'}
                </div>
              </div>
            </div>
          </div>

          {/* Event Todos */}
          <div className={`${theme.border} border rounded p-1.5`}>
            <EventTodoPanel eventId={baseId} eventTitle={event.title} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-1.5 mt-2">
          <button
            onClick={onEdit}
            disabled={isDeleting}
            className={`${theme.accent} text-white px-3 py-1.5 rounded-landing text-xs font-semibold transition-all active:scale-95 flex-1 min-h-[44px] flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
          >
            <span>✏️</span>
            <span>Edit</span>
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className={`bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-landing text-xs font-semibold transition-all active:scale-95 flex-1 min-h-[44px] flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <Icon name="delete" size="sm" className="text-white" />
                  <span>Delete</span>
                </>
              )}
            </button>
          )}
          <button
            onClick={onClose}
            disabled={isDeleting}
            className={`${theme.button} px-3 py-1.5 rounded-landing text-xs font-semibold transition-all active:scale-95 flex-1 min-h-[44px] border ${theme.border} flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
